import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Container
} from 'reactstrap';
import { getSubscriptionTransactions } from '../../helpers/apis';
import TransactionDetailDialog from './TransactionDetailDialog';

const SubscriptionTransactionsDialog = ({ open, onClose, user }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    if (open && user) {
      fetchTransactions();
    }
  }, [open, user]);

  const fetchTransactions = async () => {
    if (!user?._id) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await getSubscriptionTransactions(user._id);
      if (response?.status === 1) {
        setTransactions(response.data?.transactions || []);
      } else {
        setError(response?.message || 'Failed to fetch transactions');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedTransaction(null);
  };

  const formatAmount = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getStatusVariant = (status) => {
    const variants = {
      paid: 'success',
      draft: 'secondary',
      unpaid: 'warning',
      failed: 'danger',
      in_progress: 'info',
      void: 'secondary'
    };
    return variants[status] || 'secondary';
  };

  const getStatusLabel = (status) => {
    const labels = {
      paid: 'Paid',
      draft: 'Draft',
      unpaid: 'Unpaid',
      failed: 'Failed',
      in_progress: 'In Progress',
      void: 'Void'
    };
    return labels[status] || status;
  };

  return (
    <>
      {/* Main Transactions Dialog */}
      <Modal 
        isOpen={open} 
        toggle={onClose} 
        size="xl"
        className="modal-dialog-scrollable"
      >
        <ModalHeader toggle={onClose}>
          <i className="ri-bill-line align-middle me-2"></i>
          Subscription Transactions
          {user && (
            <small className="text-muted d-block mt-1">
              {user.firstname} {user.lastname} • {user.email}
            </small>
          )}
        </ModalHeader>
        
        <ModalBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2">Loading transactions...</div>
            </div>
          ) : error ? (
            <Alert color="danger">{error}</Alert>
          ) : transactions.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-file-search-line display-4 text-muted"></i>
              <p className="text-muted mt-3">No transactions found for this user.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Transaction ID</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>
                        <code className="text-primary" style={{ fontSize: '12px' }}>
                          {transaction.id}
                        </code>
                      </td>
                      <td>{formatDate(transaction.created)}</td>
                      <td>
                        <strong>{formatAmount(transaction.amount_paid || transaction.amount_due)}</strong>
                      </td>
                      <td>
                        <Badge 
                          color={getStatusVariant(transaction.detailedStatus || transaction.status)}
                          className="badge-label"
                        >
                          {getStatusLabel(transaction.detailedStatus || transaction.status)}
                        </Badge>
                      </td>
                      <td>{transaction.description || 'Subscription Payment'}</td>
                      <td>
                        <Button
                          color="primary"
                          size="sm"
                          outline
                          onClick={() => handleViewDetails(transaction)}
                        >
                          <i className="ri-eye-line me-1"></i>
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button color="secondary" onClick={onClose}>
            Close
          </Button>
          <Button 
            color="primary" 
            outline
            onClick={fetchTransactions}
            disabled={loading}
          >
            <i className="ri-refresh-line me-1"></i>
            Refresh
          </Button>
        </ModalFooter>
      </Modal>

      {/* Transaction Detail Dialog */}
      {selectedTransaction && (
        <TransactionDetailDialog
          open={detailDialogOpen}
          onClose={handleCloseDetailDialog}
          transaction={selectedTransaction}
          user={user}
        />
      )}
    </>
  );
};
export default SubscriptionTransactionsDialog;